/**
 * Recovery for stale hashed chunk references after a deploy.
 *
 * When a new build is published the currently open page may still request a
 * chunk filename that no longer exists on the CDN. Browsers word this failure
 * differently ("Failed to fetch dynamically imported module" in Chrome,
 * "error loading dynamically imported module" in others), so detection has to
 * cover several phrasings.
 */

const BUDGET_KEY = "vitolier:chunk-reload";
const MAX_RELOADS = 2;
const WINDOW_MS = 60_000;

const PATTERNS = [
  /loading chunk \S+ failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /failed to load module script/i,
  /unable to preload css/i,
  /dynamically imported module/i,
];

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const anyErr = error as { name?: string; message?: string };
  if (anyErr.name === "ChunkLoadError") return true;
  const msg = typeof error === "string" ? error : anyErr.message || "";
  if (!msg) return false;
  return PATTERNS.some((re) => re.test(msg));
}

interface Budget {
  count: number;
  first: number;
}

function readBudget(): Budget {
  try {
    const raw = sessionStorage.getItem(BUDGET_KEY);
    if (!raw) return { count: 0, first: 0 };
    const parsed = JSON.parse(raw) as Budget;
    if (typeof parsed?.count !== "number" || typeof parsed?.first !== "number") {
      return { count: 0, first: 0 };
    }
    return parsed;
  } catch {
    return { count: 0, first: 0 };
  }
}

function writeBudget(budget: Budget) {
  try {
    sessionStorage.setItem(BUDGET_KEY, JSON.stringify(budget));
  } catch {
    /* non-fatal */
  }
}

export function clearChunkReloadBudget() {
  try {
    sessionStorage.removeItem(BUDGET_KEY);
  } catch {
    /* non-fatal */
  }
}

/** True when another automatic reload is still allowed. */
export function canAttemptChunkReload(now: number = Date.now()): boolean {
  const { count, first } = readBudget();
  if (!count || now - first > WINDOW_MS) return true;
  return count < MAX_RELOADS;
}

/**
 * Consumes one reload attempt and reloads the page with a cache-busting
 * query parameter. Returns false when the budget is exhausted, in which case
 * the caller should surface a "new version available" message instead.
 */
export function attemptChunkReload(now: number = Date.now()): boolean {
  if (typeof window === "undefined") return false;
  if (!canAttemptChunkReload(now)) return false;

  const prev = readBudget();
  const stale = !prev.count || now - prev.first > WINDOW_MS;
  writeBudget({ count: stale ? 1 : prev.count + 1, first: stale ? now : prev.first });

  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_v", String(now));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
  return true;
}

/**
 * Global listeners for chunk failures that happen outside React rendering:
 * Vite's preload error event, plain window errors and rejected promises.
 */
export function installChunkErrorRecovery() {
  if (typeof window === "undefined") return;

  const handle = (error: unknown) => {
    if (!isChunkLoadError(error)) return false;
    return attemptChunkReload();
  };

  window.addEventListener("vite:preloadError", (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    if (handle(payload ?? event)) event.preventDefault?.();
  });

  window.addEventListener("error", (event: ErrorEvent) => {
    handle(event.error ?? event.message);
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    handle(event.reason);
  });
}
